import React, { useEffect, useState, useRef, useCallback } from "react";
import { Mic, Volume2, Loader2, Trash2, AudioLines, Music, Gamepad2, Wand2, Library, Upload, Play, Plus, PlayCircle, Square } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Repeat } from "lucide-react";

const SFX_PRESETS = [
  ["🔫 Laser blast", "sci-fi laser blast, sharp energy zap"],
  ["🪙 Coin pickup", "retro 8-bit coin pickup chime"],
  ["💥 Explosion", "big cinematic explosion with debris"],
  ["👣 Footsteps", "footsteps running on gravel"],
  ["⭐ Power-up", "magical power-up glimmer, ascending sparkle"],
  ["🏆 Victory", "triumphant victory fanfare jingle"],
];

const PACK_THEMES = [
  ["retro", "🕹️ Retro arcade"],
  ["fantasy", "🏰 Fantasy epic"],
  ["scifi", "🚀 Sci-fi"],
  ["cute", "🧸 Cute cartoon"],
  ["action", "💥 Action"],
];

const GAME_EVENTS = [
  ["bgm", "Background music", "Loops during play", Music],
  ["start", "Intro / start", "Plays once when the game starts", Mic],
  ["collect", "On collect", "Coins & pickups", Volume2],
  ["win", "On win", "Victory moment", Volume2],
  ["gameover", "On game over", "Defeat moment", Volume2],
  ["hit", "On hit / damage", "Player takes damage", Volume2],
];

const EV_LABEL = Object.fromEntries(GAME_EVENTS.map(([ev, label]) => [ev, label]));

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function VoiceSfxStudio({ gameId, config, onConfig }) {
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState("");
  const [text, setText] = useState("");
  const [ttsLoading, setTtsLoading] = useState(false);
  const [sfxPrompt, setSfxPrompt] = useState("");
  const [sfxDuration, setSfxDuration] = useState(3);
  const [sfxLoading, setSfxLoading] = useState(false);
  const [packTheme, setPackTheme] = useState("retro");
  const [packLoading, setPackLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [library, setLibrary] = useState([]);
  const [libBusyId, setLibBusyId] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [playingAll, setPlayingAll] = useState(false);
  const [seq, setSeq] = useState([]);
  const [seqIdx, setSeqIdx] = useState(-1);
  const stopAllRef = useRef(false);
  const jumpRef = useRef(null);
  const finishCurrentRef = useRef(null);
  const previewRef = useRef(null);
  const fileRef = useRef(null);

  const audioMap = (config && config.audio) || {};

  const loadAssets = useCallback(async () => {
    try {
      const { data } = await api.get(`/audio/assets/${gameId}`);
      setAssets(data.assets || []);
    } catch { /* ignore */ }
  }, [gameId]);

  const loadLibrary = useCallback(async () => {
    try {
      const { data } = await api.get("/audio/library");
      setLibrary(data.categories || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audio/voices");
        setVoices(data.voices || []);
        if (data.voices?.length) setVoiceId(data.voices[0].voice_id);
      } catch { /* ignore */ }
    })();
    loadAssets();
    loadLibrary();
  }, [loadAssets, loadLibrary]);

  const previewVoice = (url) => {
    if (!url || !previewRef.current) return;
    try { previewRef.current.src = url; previewRef.current.currentTime = 0; previewRef.current.play().catch(() => {}); } catch { /* ignore */ }
  };
  const stopPreview = () => { try { previewRef.current?.pause(); } catch { /* ignore */ } };

  const clipUrlFor = (slot) => slot && (slot.url || assets.find((a) => a.id === slot.id)?.url);
  const SOUNDSCAPE_ORDER = ["start", "collect", "hit", "win", "gameover", "bgm"];
  const playUrl = (url, maxMs) => new Promise((resolve) => {
    const a = previewRef.current;
    if (!a || !url) return resolve();
    let done = false;
    let t;
    const finish = () => { if (done) return; done = true; a.onended = null; clearTimeout(t); finishCurrentRef.current = null; resolve(); };
    finishCurrentRef.current = finish;
    a.onended = finish;
    t = setTimeout(() => { try { a.pause(); } catch { /* ignore */ } finish(); }, maxMs);
    try { a.src = url; a.currentTime = 0; a.play().catch(finish); } catch { finish(); }
  });
  const endCurrent = () => { try { previewRef.current?.pause(); } catch { /* ignore */ } if (finishCurrentRef.current) finishCurrentRef.current(); };
  const skipTo = (i) => { jumpRef.current = i; endCurrent(); };
  const playAll = async () => {
    if (playingAll) { stopAllRef.current = true; endCurrent(); setPlayingAll(false); setNowPlaying(null); setSeqIdx(-1); return; }
    const wired = SOUNDSCAPE_ORDER.filter((ev) => clipUrlFor(audioMap[ev]));
    if (!wired.length) { toast.error("Wire a sound to an event first."); return; }
    setSeq(wired); setPlayingAll(true); stopAllRef.current = false; jumpRef.current = null;
    let i = 0;
    while (i < wired.length) {
      if (stopAllRef.current) break;
      const ev = wired[i];
      setSeqIdx(i); setNowPlaying(ev);
      await playUrl(`${BACKEND}${clipUrlFor(audioMap[ev])}`, ev === "bgm" ? 4500 : 3000);
      if (stopAllRef.current) break;
      if (jumpRef.current != null) { i = jumpRef.current; jumpRef.current = null; continue; }
      i += 1;
      if (i < wired.length) await new Promise((r) => setTimeout(r, 140));
    }
    setNowPlaying(null); setPlayingAll(false); setSeqIdx(-1); stopAllRef.current = false;
  };

  const genVoice = async () => {
    if (!text.trim() || !voiceId || ttsLoading) return;
    setTtsLoading(true);
    try {
      await api.post("/audio/tts", { game_id: gameId, text: text.trim(), voice_id: voiceId });
      toast.success("Voice line generated 🎙️");
      setText("");
      loadAssets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Voice generation failed"); }
    finally { setTtsLoading(false); }
  };

  const genSfx = async (prompt) => {
    const p = (typeof prompt === "string" ? prompt : sfxPrompt).trim();
    if (!p || sfxLoading) return;
    setSfxLoading(true);
    try {
      await api.post("/audio/sfx", { game_id: gameId, prompt: p, duration_seconds: Number(sfxDuration) });
      toast.success("Sound effect generated 🔊");
      loadAssets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "SFX generation failed"); }
    finally { setSfxLoading(false); }
  };

  const applySfxPreset = (p) => { setSfxPrompt(p); genSfx(p); };

  const genPack = async () => {
    if (packLoading) return;
    setPackLoading(true);
    try {
      const { data } = await api.post("/audio/pack", { game_id: gameId, theme: packTheme });
      if (onConfig && data.config) onConfig(data.config);
      await loadAssets();
      toast.success(`Sound pack ready — ${data.count} clips wired into gameplay 🎼`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sound pack generation failed");
    } finally { setPackLoading(false); }
  };

  const assign = async (event, audioId) => {
    try {
      const { data } = await api.post("/audio/assign", { game_id: gameId, event, audio_id: audioId });
      if (onConfig && data.config) onConfig(data.config);
      toast.success(audioId ? "Sound wired into gameplay 🎮" : "Assignment cleared");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not assign"); }
  };

  const updateSettings = async (event, patch) => {
    try {
      const { data } = await api.post("/audio/event-settings", { game_id: gameId, event, ...patch });
      if (onConfig && data.config) onConfig(data.config);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update"); }
  };

  const del = async (id) => {
    try {
      const { data } = await api.delete(`/audio/asset/${id}`);
      setAssets((a) => a.filter((x) => x.id !== id));
      if (onConfig && data.config) onConfig(data.config);
      toast.success("Removed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markCached = (id, url) => setLibrary((cats) => cats.map((c) => ({
    ...c, items: c.items.map((x) => (x.id === id ? { ...x, cached: true, preview_url: url } : x)),
  })));

  const previewLibrary = async (it) => {
    setLibBusyId(it.id);
    try {
      let url = it.preview_url;
      if (!url) {
        const { data } = await api.post("/audio/library/preview", { preset_id: it.id }, { timeout: 45000 });
        url = data.url; markCached(it.id, url);
      }
      previewVoice(`${BACKEND}${url}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't play that sound"); }
    finally { setLibBusyId(null); }
  };

  const addLibrary = async (it) => {
    setLibBusyId(it.id);
    try {
      const { data } = await api.post("/audio/library/assign", { game_id: gameId, preset_id: it.id }, { timeout: 45000 });
      if (onConfig && data.config) onConfig(data.config);
      markCached(it.id, `/api/audio/library/file/${it.id}.mp3`);
      await loadAssets();
      toast.success(`Added “${it.name}” to ${data.event} 🎮`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't add that sound"); }
    finally { setLibBusyId(null); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Keep it under 4 MB."); return; }
    setUploadBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      const { data } = await api.post("/audio/upload", { game_id: gameId, name: file.name.replace(/\.[^.]+$/, ""), data: dataUrl });
      if (onConfig && data.config) onConfig(data.config);
      await loadAssets();
      toast.success("Uploaded 🎧 — wire it to an event below");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploadBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="voice-sfx-studio">
      <audio ref={previewRef} className="hidden" />

      {/* One-tap Sound Pack */}
      <div className="rounded-xl border border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-500/15 via-violet-500/10 to-transparent p-3 space-y-2.5" data-testid="sound-pack-panel">
        <div className="text-xs font-bold flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5 text-fuchsia-400" /> One-tap Sound Pack
          <Badge variant="secondary" className="ml-auto text-[10px]">6 clips</Badge>
        </div>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Instantly score your whole game — background music + start, collect, win, game-over & hit sounds, all matched to a theme and auto-wired to gameplay.</p>
        <div className="flex flex-wrap gap-1.5" data-testid="pack-themes">
          {PACK_THEMES.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setPackTheme(key)} disabled={packLoading}
              data-testid={`pack-theme-${key}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${packTheme === key ? "border-fuchsia-500/70 bg-fuchsia-500/25 text-fuchsia-200 font-semibold" : "border-fuchsia-500/30 bg-fuchsia-500/5 text-[hsl(var(--muted-foreground))] hover:bg-fuchsia-500/15"}`}>
              {label}
            </button>
          ))}
        </div>
        <Button onClick={genPack} disabled={packLoading} size="sm"
          className="w-full rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-500" data-testid="generate-pack-btn">
          {packLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scoring your game… (~20s)</> : <><Wand2 className="w-3.5 h-3.5" /> Generate Sound Pack</>}
        </Button>
        <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center">Generates 6 clips · uses ElevenLabs credits · overwrites current event assignments</p>
      </div>

      {/* Voice narration */}
      <div className="rounded-xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 to-transparent p-3 space-y-2.5" data-testid="voice-panel">
        <div className="text-xs font-bold flex items-center gap-1.5"><Mic className="w-3.5 h-3.5 text-indigo-400" /> AI Voice Narration
          <Badge variant="secondary" className="ml-auto text-[10px]">ElevenLabs</Badge>
        </div>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Turn any line into pro voiceover — narrator intros, character dialogue, tutorials. <span className="text-indigo-300">Hover a voice to preview.</span></p>
        <Select value={voiceId} onValueChange={setVoiceId}>
          <SelectTrigger className="h-8 text-sm" data-testid="voice-select"><SelectValue placeholder="Pick a voice" /></SelectTrigger>
          <SelectContent>
            {voices.map((v) => (
              <SelectItem key={v.voice_id} value={v.voice_id}
                onMouseEnter={() => previewVoice(v.preview_url)} onMouseLeave={stopPreview}
                data-testid={`voice-option-${v.voice_id}`}>
                {v.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={1000}
          placeholder="e.g. Welcome, brave adventurer, to the neon city…" className="text-sm" data-testid="voice-text" />
        <Button onClick={genVoice} disabled={ttsLoading || !text.trim()} size="sm"
          className="w-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500" data-testid="generate-voice-btn">
          {ttsLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</> : <><Mic className="w-3.5 h-3.5" /> Generate voice</>}
        </Button>
      </div>

      {/* Sound effects */}
      <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-3 space-y-2.5" data-testid="sfx-panel">
        <div className="text-xs font-bold flex items-center gap-1.5"><Volume2 className="w-3.5 h-3.5 text-amber-400" /> AI Sound Effects
          <Badge variant="secondary" className="ml-auto text-[10px]">ElevenLabs</Badge>
        </div>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Describe any sound — Studio generates it. Coins, lasers, explosions & more.</p>
        <div className="flex gap-1.5">
          <Input value={sfxPrompt} onChange={(e) => setSfxPrompt(e.target.value)} data-testid="sfx-prompt"
            placeholder="e.g. glass shatter, sword clash" className="text-sm h-8" />
          <Select value={String(sfxDuration)} onValueChange={setSfxDuration}>
            <SelectTrigger className="h-8 w-20 text-sm shrink-0" data-testid="sfx-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 5, 8].map((s) => <SelectItem key={s} value={String(s)}>{s}s</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="sfx-presets">
          {SFX_PRESETS.map(([label, prompt]) => (
            <button key={label} type="button" onClick={() => applySfxPreset(prompt)} disabled={sfxLoading}
              data-testid={`sfx-preset-${label.replace(/[^a-z]/gi, "").toLowerCase()}`}
              className="text-[11px] px-2.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors disabled:opacity-50">
              {label}
            </button>
          ))}
        </div>
        <Button onClick={() => genSfx()} disabled={sfxLoading || !sfxPrompt.trim()} size="sm"
          className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500" data-testid="generate-sfx-btn">
          {sfxLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</> : <><Volume2 className="w-3.5 h-3.5" /> Generate sound effect</>}
        </Button>
      </div>

      {/* Curated Sound FX Library */}
      <div className="rounded-xl border border-sky-500/30 bg-gradient-to-br from-sky-500/10 to-transparent p-3 space-y-2.5" data-testid="sfx-library-panel">
        <div className="text-xs font-bold flex items-center gap-1.5"><Library className="w-3.5 h-3.5 text-sky-400" /> Sound FX Library
          <Badge variant="secondary" className="ml-auto text-[10px]">Curated</Badge>
        </div>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Real recorded-quality effects — <span className="text-sky-300">tap to preview</span>, then <span className="text-sky-300">＋</span> to wire it into gameplay.</p>
        {library.length === 0 ? (
          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Library unavailable right now.</p>
        ) : library.map((cat) => (
          <div key={cat.name} className="space-y-1" data-testid={`sfx-lib-cat-${cat.name.replace(/[^a-z]/gi, "").toLowerCase()}`}>
            <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{cat.name}</div>
            <div className="flex flex-wrap gap-1.5">
              {cat.items.map((it) => (
                <div key={it.id} className="flex items-center rounded-full border border-sky-500/40 bg-sky-500/10 overflow-hidden" data-testid={`sfx-lib-${it.id}`}>
                  <button type="button" onClick={() => previewLibrary(it)} disabled={libBusyId === it.id}
                    className="text-[11px] pl-2.5 pr-1.5 py-1 text-sky-200 hover:bg-sky-500/20 flex items-center gap-1 disabled:opacity-50"
                    data-testid={`sfx-lib-preview-${it.id}`}>
                    {libBusyId === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} {it.name}
                  </button>
                  <button type="button" onClick={() => addLibrary(it)} disabled={libBusyId === it.id}
                    title={`Add to ${it.event}`} data-testid={`sfx-lib-add-${it.id}`}
                    className="px-1.5 py-1 border-l border-sky-500/40 text-sky-300 hover:bg-sky-500/25 disabled:opacity-50">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Upload your own recording */}
      <div className="rounded-xl border border-teal-500/30 bg-gradient-to-br from-teal-500/10 to-transparent p-3 space-y-2.5" data-testid="sfx-upload-panel">
        <div className="text-xs font-bold flex items-center gap-1.5"><Upload className="w-3.5 h-3.5 text-teal-400" /> Upload your own
          <Badge variant="secondary" className="ml-auto text-[10px]">mp3 · wav · ogg</Badge>
        </div>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Drop in a real recording (up to 4 MB). It joins your library below, ready to wire to any event.</p>
        <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={onUpload} data-testid="sfx-upload-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploadBusy} size="sm"
          className="w-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500" data-testid="sfx-upload-btn">
          {uploadBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> Choose an audio file</>}
        </Button>
      </div>

      {/* Wire audio into gameplay events */}
      <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent p-3 space-y-2.5" data-testid="audio-assign-panel">
        <div className="text-xs font-bold flex items-center gap-1.5"><Gamepad2 className="w-3.5 h-3.5 text-emerald-400" /> Play in game
          <button type="button" onClick={playAll} data-testid="soundscape-play-all"
            className="ml-auto flex items-center gap-1 rounded-full border border-emerald-500/50 bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 hover:bg-emerald-500/25 transition-colors">
            {playingAll ? <><Square className="w-3 h-3" /> Stop</> : <><PlayCircle className="w-3 h-3" /> Play soundscape</>}
          </button>
        </div>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Assign your clips to game moments — they’ll play in the preview & published game. Tap <span className="text-emerald-300">Play soundscape</span> to hear them all in sequence.</p>
        {playingAll && seq.length > 0 && (
          <div className="space-y-1" data-testid="soundscape-timeline">
            <div className="flex gap-1">
              {seq.map((ev, i) => (
                <button key={ev} type="button" onClick={() => skipTo(i)} data-testid={`soundscape-seg-${ev}`}
                  title={`Skip to ${EV_LABEL[ev] || ev}`} className="flex-1 py-1 group">
                  <span className={`block h-1.5 rounded-full transition-colors ${i < seqIdx ? "bg-emerald-500/70" : i === seqIdx ? "bg-emerald-400 animate-pulse" : "bg-[hsl(var(--secondary))] group-hover:bg-emerald-500/40"}`} />
                </button>
              ))}
            </div>
            <div className="text-[10px] text-emerald-300 font-semibold text-center" data-testid="soundscape-now">▶ {EV_LABEL[seq[seqIdx]] || "…"}</div>
          </div>
        )}
        {assets.length === 0 ? (
          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Generate a voice line or SFX above, then wire it to an event here.</p>
        ) : GAME_EVENTS.map(([ev, label, hint, Icon]) => {
          const slot = audioMap[ev];
          const vol = typeof slot?.volume === "number" ? slot.volume : (ev === "bgm" ? 0.4 : 0.9);
          const looping = typeof slot?.loop === "boolean" ? slot.loop : (ev === "bgm");
          return (
          <div key={ev} className={`space-y-1.5 rounded-lg transition-colors ${nowPlaying === ev ? "bg-emerald-500/10 ring-1 ring-emerald-500/40 p-1.5 -m-1.5" : ""}`} data-testid={`assign-row-${ev}`}>
            <div className="flex items-center gap-2">
              <Icon className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold leading-tight truncate">{label}</div>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))] leading-tight">{hint}</div>
              </div>
              <Select value={slot?.id || "none"} onValueChange={(val) => assign(ev, val === "none" ? null : val)}>
                <SelectTrigger className="h-8 w-36 text-xs shrink-0" data-testid={`assign-select-${ev}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.kind === "voice" ? "🎙️ " : "🔊 "}{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {slot?.id && (
              <div className="flex items-center gap-2 pl-5" data-testid={`assign-controls-${ev}`}>
                <button type="button" data-testid={`preview-event-${ev}`}
                  onClick={() => { const u = slot.url || assets.find((a) => a.id === slot.id)?.url; if (u) previewVoice(`${BACKEND}${u}`); }}
                  title="Hear this sound" className="shrink-0 w-6 h-6 grid place-items-center rounded-full bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/35">
                  <Play className="w-3 h-3" />
                </button>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0">Vol</span>
                <Slider min={0} max={100} step={5} defaultValue={[Math.round(vol * 100)]} key={`${ev}-${Math.round(vol * 100)}`}
                  onValueCommit={(v) => updateSettings(ev, { volume: (v[0] || 0) / 100 })}
                  className="w-24" data-testid={`volume-slider-${ev}`} />
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] w-7 shrink-0 tabular-nums">{Math.round(vol * 100)}%</span>
                <button type="button" onClick={() => updateSettings(ev, { loop: !looping })}
                  data-testid={`loop-toggle-${ev}`}
                  className={`ml-auto text-[10px] px-2 py-1 rounded-full border flex items-center gap-1 transition-colors shrink-0 ${looping ? "border-emerald-500/60 bg-emerald-500/20 text-emerald-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]"}`}>
                  <Repeat className="w-3 h-3" /> Loop {looping ? "on" : "off"}
                </button>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Audio library */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-3 space-y-2" data-testid="audio-library">
        <div className="text-xs font-bold flex items-center gap-1.5"><AudioLines className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Audio Library
          <Badge variant="secondary" className="ml-auto text-[10px]">{assets.length}</Badge>
        </div>
        {assets.length === 0 ? (
          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">No audio yet — generate a voice line or sound effect above.</p>
        ) : assets.map((a) => (
          <div key={a.id} className="rounded-lg border border-[hsl(var(--border))]/60 bg-[hsl(var(--secondary))]/30 p-2 space-y-1.5" data-testid={`audio-asset-${a.id}`}>
            <div className="flex items-center gap-2">
              {a.kind === "voice" ? <Mic className="w-3 h-3 text-indigo-400 shrink-0" /> : <Volume2 className="w-3 h-3 text-amber-400 shrink-0" />}
              <span className="text-[11px] font-semibold truncate flex-1">{a.name}</span>
              <button onClick={() => del(a.id)} data-testid={`delete-audio-${a.id}`} className="text-[hsl(var(--muted-foreground))] hover:text-rose-400 shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <audio controls preload="none" src={`${BACKEND}${a.url}`} className="w-full h-8" data-testid={`audio-player-${a.id}`} />
          </div>
        ))}
      </div>
    </div>
  );
}
